import { Select, SelectOptionType, TextInput } from '@octano/global-ui';
import {
  Fragment,
  ReactChild,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import {
  AcademicLevelListItem,
  SchoolsListItem,
} from '../../../../api/requests/schools';
import { SectionTitle } from '../../../../components/text';
import { useLayoutState } from '../../../../hooks/useLayoutState';
import { useValidations } from '../../../../hooks/useValidations';
import { ModalityListItem } from '../../../../types/ModalityTypes';
import { PeriodType } from '../../../../types/PeriodType';
import { formatOptions } from '../../../../utils/selectFormat';
import StudyPlanFormSies from './StudyPlanFormSies';

export interface StudyPlanValues {
  code: string;
  siesCode: string;
  name: string;
  degreeTitle: string | null;
  school: SelectOptionType | null;
  modality: SelectOptionType | null;
  periodType: SelectOptionType | null;
  academicLevel: SelectOptionType | null;
  careerLevel: SelectOptionType | null;
  // SIES
  admissionRequirement?: SelectOptionType | null;
  recognizedSemesters?: number;
  academicArea?: SelectOptionType | null;
  highSchoolDiplomaAdmission?: SelectOptionType | null;
  highSchoolGradesAdmission?: SelectOptionType | null;
  minimumHighSchoolGPA?: number;
  priorLearningRecognitionAdmission?: SelectOptionType | null;
  workExperienceAdmission?: SelectOptionType | null;
}

export interface RecordToEditBody {
  code: string;
  siesCode: string;
  name: string;
  degreeTitle: string | null;
  school: SelectOptionType;
  modality: SelectOptionType;
  periodType: SelectOptionType;
  academicLevel: SelectOptionType;
  careerLevel: SelectOptionType | null;
  academicArea?: SelectOptionType | null;
  recognizedSemesters?: number;
  admissionRequirement?: SelectOptionType | null;
  highSchoolDiplomaAdmission?: SelectOptionType | null;
  highSchoolGradesAdmission?: SelectOptionType | null;
  minimumHighSchoolGPA?: number;
  priorLearningRecognitionAdmission?: SelectOptionType | null;
  workExperienceAdmission?: SelectOptionType | null;
}

const getDefaultValues = (defaultValues?: RecordToEditBody) =>
  defaultValues
    ? {
        code: defaultValues.code,
        siesCode: defaultValues.siesCode?.trim() || '',
        name: defaultValues.name,
        school: defaultValues.school,
        modality: defaultValues.modality,
        periodType: defaultValues.periodType,
        academicLevel: defaultValues.academicLevel,
        careerLevel: defaultValues.careerLevel,
        degreeTitle: defaultValues.degreeTitle,
        academicArea: defaultValues.academicArea,
        recognizedSemesters: defaultValues.recognizedSemesters,
        admissionRequirement: defaultValues.admissionRequirement,
        highSchoolDiplomaAdmission: defaultValues.highSchoolDiplomaAdmission,
        highSchoolGradesAdmission: defaultValues.highSchoolGradesAdmission,
        minimumHighSchoolGPA: defaultValues.minimumHighSchoolGPA,
        priorLearningRecognitionAdmission:
          defaultValues.priorLearningRecognitionAdmission,
        workExperienceAdmission: defaultValues.workExperienceAdmission,
      }
    : {
        code: '',
        siesCode: '',
        name: '',
        degreeTitle: '',
        school: null,
        modality: null,
        periodType: null,
        academicLevel: null,
        careerLevel: null,
        academicArea: null,
        recognizedSemesters: 0,
        admissionRequirement: null,
        highSchoolDiplomaAdmission: null,
        highSchoolGradesAdmission: null,
        minimumHighSchoolGPA: 0,
        priorLearningRecognitionAdmission: null,
        workExperienceAdmission: null,
      };

export interface StudyPlanFormProps {
  modalities: ModalityListItem[];
  periodTypes: PeriodType[];
  schools: SchoolsListItem[];
  academicLevels: AcademicLevelListItem[];
  academicAreas: AcademicLevelListItem[];
  admissionRequirements: AcademicLevelListItem[];
  defaultValues?: RecordToEditBody;
  children: (props: { isSubmitting: boolean; isDirty?: boolean }) => ReactChild;
  onSubmit: (
    values: StudyPlanValues,
  ) => Promise<'CONNECTION' | 'HTTP_ERROR' | 'NOT_UNIQUE_CODE' | undefined>;
  readOnly?: boolean;
  isUpdate?: boolean;
  siesEnabled?: boolean;
}

const StudyPlanForm = (props: StudyPlanFormProps) => {
  const {
    onSubmit: onSubmitProp,
    children,
    modalities,
    periodTypes,
    schools,
    academicLevels,
    academicAreas,
    admissionRequirements,
    defaultValues,
    readOnly,
    isUpdate,
    siesEnabled,
  } = props;

  const { showErrorModal } = useLayoutState();

  const { t } = useTranslation();
  const prefix = 'studyPlans.form';

  const methods = useForm<StudyPlanValues>({
    mode: 'onSubmit',
    defaultValues: getDefaultValues(defaultValues),
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    control,
    setError,
    setValue,
    watch,
  } = methods;

  const [selectedAcademicLevel] = watch(['academicLevel']);
  const [careerLevels, setFilteredCareerLevels] = useState<SelectOptionType[]>(
    [],
  );

  const { msgValidations } = useValidations();
  const onSubmit = useCallback(
    async (formValues: StudyPlanValues) => {
      const response = await onSubmitProp(formValues);
      if (response === 'CONNECTION') {
        showErrorModal();
      } else if (response === 'HTTP_ERROR') {
        setError('code', {
          message: t('common.errors.codeError', { code: formValues.code }),
        });
      }
    },
    [onSubmitProp, showErrorModal, setError, t],
  );

  useEffect(() => {
    let filtered: SelectOptionType[] = [];
    if (selectedAcademicLevel?.careerLevels) {
      filtered = [...formatOptions(selectedAcademicLevel.careerLevels)];
    }
    setFilteredCareerLevels(filtered);
  }, [selectedAcademicLevel]);

  const prevCampusRef = useRef(selectedAcademicLevel);
  useEffect(() => {
    const prevCampus = prevCampusRef.current;
    if (prevCampus?.value !== selectedAcademicLevel?.value) {
      setValue('careerLevel', null);
      prevCampusRef.current = selectedAcademicLevel;
    }
  }, [selectedAcademicLevel, setValue]);

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12}>
              <SectionTitle text={t(`${prefix}.data`)} />
              <Row>
                <Col xs={12} md={6}>
                  <div className="pb-3">
                    <TextInput
                      name="code"
                      label={t(`${prefix}.planCode`)}
                      control={control}
                      rules={{
                        required: msgValidations.required,
                        maxLength: {
                          value: 50,
                          message: t('common.validations.maxLength', {
                            length: 50,
                          }),
                        },
                        pattern: {
                          value: /^[0-9a-zA-Z]+$/,
                          message: t('common.validations.invalidAlphanumeric'),
                        },
                      }}
                      disabled={readOnly}
                    />
                  </div>
                </Col>
                {!!siesEnabled && (
                  <Col xs={12} md={6}>
                    <div className="pb-3">
                      <TextInput
                        name="siesCode"
                        label={t('common.forms.siesCode')}
                        control={control}
                        disabled={readOnly}
                      />
                    </div>
                  </Col>
                )}
                <Col xs={12} md={siesEnabled ? 12 : 6} className="pb-3">
                  <TextInput
                    name="name"
                    label={t('common.forms.name')}
                    control={control}
                    rules={{
                      required: msgValidations.required,
                      minLength: {
                        value: 5,
                        message: t('common.validations.minLength', {
                          length: 5,
                        }),
                      },
                      maxLength: {
                        value: 255,
                        message: t('common.validations.maxLength', {
                          length: 255,
                        }),
                      },
                    }}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} className="pb-3">
                  <Select
                    name="school"
                    label={t('common.forms.school')}
                    options={schools.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="modality"
                    label={t('common.forms.modality')}
                    options={modalities.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="periodType"
                    label={t('common.forms.periodType')}
                    options={periodTypes.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly || isUpdate}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="academicLevel"
                    label={t('common.forms.academicLevel')}
                    options={academicLevels.map((e) => ({
                      label: e.name,
                      value: e.id,
                      careerLevels: e.careerLevels,
                    }))}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Select
                    name="careerLevel"
                    label={t('common.forms.careerLevel')}
                    options={careerLevels}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <TextInput
                    name="degreeTitle"
                    label={t('common.forms.degreeTitle')}
                    control={control}
                    rules={{
                      required: msgValidations.required,
                      minLength: {
                        value: 5,
                        message: t('common.validations.minLength', {
                          length: 5,
                        }),
                      },
                      maxLength: {
                        value: 255,
                        message: t('common.validations.maxLength', {
                          length: 255,
                        }),
                      },
                    }}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={4} className="pb-3"></Col>
          </Row>
          {siesEnabled && (
            <StudyPlanFormSies
              readOnly={readOnly}
              academicAreas={academicAreas}
              admissionRequirements={admissionRequirements}
            />
          )}

          {children({ isSubmitting, isDirty })}
        </Form>
      </FormProvider>
    </Fragment>
  );
};

export default StudyPlanForm;
